import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';

import { ApiMethod, AuthEndPoints } from '../../../helper/apiconstant';
import { Httpservice } from '../../../services/httpservice';
import { Commonservice } from '../../../services/commonservice';
import { DEFAULT_DASHBOARD_USER } from '../../dashboard/dashboard.data';
import { DashboardHeaderComponent } from '../../structure/header/header';
import { DashboardSidebarComponent } from '../../structure/sidebar/sidebar';
import {
  ManageCountryDataBlockResponseItem,
  ManageCountryDataDistrictResponseItem,
  ManageCountryDataLevelType,
  ManageCountryDataRow,
  ManageCountryDataStateResponseItem,
} from './manage-country-data.interfaces';

type ManageCountryDataModalMode = 'edit' | 'view';

type CountryDataLoadedBlocks = ManageCountryDataRow[];

@Component({
  selector: 'app-manage-country-data',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, DashboardSidebarComponent, DashboardHeaderComponent],
  templateUrl: './manage-country-data.html',
  styleUrl: './manage-country-data.scss',
})
export class ManageCountryDataPage implements OnInit {
  protected readonly user = DEFAULT_DASHBOARD_USER;
  protected readonly filterForm: UntypedFormGroup;
  protected readonly editForm: UntypedFormGroup;

  protected sidebarCollapsed = false;
  protected mobileSidebarOpen = false;
  protected isLoading = false;
  protected isExporting = false;
  protected isCountryActionMenuOpen = false;
  protected isSaving = false;
  protected editModalOpen = false;
  protected formSubmitted = false;

  protected stateRows: ManageCountryDataRow[] = [];
  protected visibleRows: ManageCountryDataRow[] = [];

  protected selectedRow: ManageCountryDataRow | null = null;
  protected modalMode: ManageCountryDataModalMode = 'edit';
  protected expandedStateId: number | null = null;
  protected expandedDistrictId: number | null = null;
  protected loadingStateId: number | null = null;
  protected loadingDistrictId: number | null = null;

  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly districtsByState = new Map<number, ManageCountryDataRow[]>();
  private readonly blocksByDistrict = new Map<number, CountryDataLoadedBlocks>();

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly httpSvc: Httpservice,
    private readonly commonSvc: Commonservice,
  ) {
    this.filterForm = this.fb.group({
      search: [''],
    });
    this.editForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
    });
  }

  ngOnInit(): void {
    this.loadStates();
    this.filterForm
      .get('search')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.resetHierarchy();
        this.loadStates();
      });
  }

  protected handleMenuToggle(): void {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      return;
    }

    this.mobileSidebarOpen = !this.mobileSidebarOpen;
  }

  protected closeMobileSidebar(): void {
    this.mobileSidebarOpen = false;
  }

  protected clearFilters(): void {
    this.filterForm.reset({ search: '' }, { emitEvent: false });
    this.isCountryActionMenuOpen = false;
    this.resetHierarchy();
    this.loadStates();
  }

  protected toggleCountryActionMenu(): void {
    if (this.isLoading || this.isExporting || this.isSaving) {
      return;
    }

    this.isCountryActionMenuOpen = !this.isCountryActionMenuOpen;
  }

  protected handleExportToExcel(): void {
    if (this.isExporting) {
      return;
    }

    this.isCountryActionMenuOpen = false;
    this.isExporting = true;
    const query = this.buildQueryString({ search: this.getSearchValue() });
    this.httpSvc
      .downloadfile(AuthEndPoints.EXPORT_COUNTRY_DATA, ApiMethod.GET, query)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isExporting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => this.downloadBlob(blob, this.buildExportFileName()),
        error: (error) => this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || 'Failed to export country data.'),
      });
  }

  protected getSearchValue(): string {
    return String(this.filterForm.get('search')?.value ?? '').trim();
  }

  protected openEditModal(row: ManageCountryDataRow): void {
    this.modalMode = 'edit';
    this.selectedRow = row;
    this.editForm.enable({ emitEvent: false });
    this.editForm.reset({
      name: row.name,
    });
    this.formSubmitted = false;
    this.editModalOpen = true;
  }

  protected openViewModal(row: ManageCountryDataRow): void {
    this.modalMode = 'view';
    this.selectedRow = row;
    this.editForm.enable({ emitEvent: false });
    this.editForm.reset({
      name: row.name,
    });
    this.formSubmitted = false;
    this.editModalOpen = true;
    this.editForm.disable({ emitEvent: false });
  }

  protected trackByRow(_: number, row: ManageCountryDataRow): string {
    return `${row.level_type}-${row.id}`;
  }

  protected hasChildren(row: ManageCountryDataRow): boolean {
    return row.has_children;
  }

  protected isExpanded(row: ManageCountryDataRow): boolean {
    if (row.level_type === 'state') {
      return this.expandedStateId === row.id;
    }

    if (row.level_type === 'district') {
      return this.expandedDistrictId === row.id;
    }

    return false;
  }

  protected isLoadingRow(row: ManageCountryDataRow): boolean {
    return (row.level_type === 'state' && this.loadingStateId === row.id) || (row.level_type === 'district' && this.loadingDistrictId === row.id);
  }

  protected getRowPadding(row: ManageCountryDataRow): Record<string, string> {
    return {
      paddingLeft: `${0.35 + row.depth * 1.45}rem`,
    };
  }

  protected toggleRow(row: ManageCountryDataRow): void {
    if (row.level_type === 'state') {
      this.toggleStateRow(row);
      return;
    }

    if (row.level_type === 'district') {
      this.toggleDistrictRow(row);
    }
  }

  protected closeEditModal(): void {
    this.editModalOpen = false;
    this.selectedRow = null;
    this.formSubmitted = false;
    this.modalMode = 'edit';
    this.editForm.enable({ emitEvent: false });
    this.editForm.reset({ name: '' });
  }

  protected saveEdit(): void {
    if (this.modalMode === 'view') {
      return;
    }

    if (this.editForm.invalid || !this.selectedRow) {
      this.formSubmitted = true;
      this.editForm.markAllAsTouched();
      return;
    }

    const nextName = String(this.editForm.get('name')?.value ?? '').trim();
    if (!nextName) {
      return;
    }

    const payload = {
      id: this.selectedRow.id,
      level_type: this.selectedRow.level_type,
      name: nextName,
    };

    this.isSaving = true;
    this.httpSvc
      .requestCall(AuthEndPoints.EDIT_COUNTRY_DATA, ApiMethod.PATCH, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isSaving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.applyEditLocally(payload.id, payload.level_type, nextName);
          this.closeEditModal();
          this.commonSvc.notifyApiResponse(response, 'Country data updated successfully', true);
        },
        error: (error) => this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || 'Failed to update country data.'),
      });
  }

  protected getLevelLabel(row: ManageCountryDataRow): string {
    switch (row.level_type) {
      case 'state':
        return 'Province';
      case 'district':
        return 'District';
      default:
        return 'Block Level';
    }
  }

  protected getEmptyMessage(): string {
    return this.isLoading ? 'Loading country data...' : 'No country data found';
  }

  private resetHierarchy(): void {
    this.expandedStateId = null;
    this.expandedDistrictId = null;
    this.loadingStateId = null;
    this.loadingDistrictId = null;
    this.districtsByState.clear();
    this.blocksByDistrict.clear();
    this.visibleRows = [];
  }

  private toggleStateRow(row: ManageCountryDataRow): void {
    if (this.expandedStateId === row.id) {
      this.expandedStateId = null;
      this.expandedDistrictId = null;
      this.loadingStateId = null;
      this.loadingDistrictId = null;
      this.visibleRows = this.buildVisibleRows();
      return;
    }

    this.expandedStateId = row.id;
    this.expandedDistrictId = null;
    this.loadingDistrictId = null;

    if (this.districtsByState.has(row.id)) {
      this.visibleRows = this.buildVisibleRows();
      return;
    }

    this.loadingStateId = row.id;
    const search = this.getSearchValue();
    this.httpSvc
      .requestCall(this.buildApiUrl(AuthEndPoints.GET_COUNTRY_DATA_DISTRICTS_LIST, { state_id: row.id, search }), ApiMethod.GET, '')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingStateId = null;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.districtsByState.set(row.id, this.extractDistrictRows(response?.value?.districts ?? response?.districts ?? [], row.id));
          this.visibleRows = this.buildVisibleRows();
        },
        error: (error) => {
          this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || 'Failed to load districts.');
          this.districtsByState.set(row.id, []);
          this.visibleRows = this.buildVisibleRows();
        },
      });
  }

  private toggleDistrictRow(row: ManageCountryDataRow): void {
    if (this.expandedDistrictId === row.id) {
      this.expandedDistrictId = null;
      this.visibleRows = this.buildVisibleRows();
      return;
    }

    this.expandedDistrictId = row.id;
    this.loadBlocks(row.id);
  }

  private loadStates(): void {
    this.isLoading = true;
    const search = this.getSearchValue();
    this.httpSvc
      .requestCall(this.buildApiUrl(AuthEndPoints.GET_COUNTRY_DATA_STATES_LIST, { search }), ApiMethod.GET, '')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.stateRows = this.extractStateRows(response?.value?.states ?? response?.states ?? []);
          this.visibleRows = this.buildVisibleRows();
        },
        error: (error) => {
          this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || 'Failed to load states.');
          this.stateRows = [];
          this.visibleRows = [];
        },
      });
  }

  private loadBlocks(districtId: number): void {
    this.loadingDistrictId = districtId;
    const query = this.buildQueryString({
      district_id: districtId,
      search: this.getSearchValue(),
    });

    this.httpSvc
      .requestCall(`${AuthEndPoints.GET_COUNTRY_DATA_BLOCK_LIST}${query}`, ApiMethod.GET, '')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingDistrictId = null;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          const rows = this.extractBlockRows(response?.value?.blocks ?? response?.blocks ?? [], districtId);
          this.blocksByDistrict.set(districtId, rows);
          this.visibleRows = this.buildVisibleRows();
        },
        error: (error) => {
          this.commonSvc.showErrorToastr(this.getApiErrorMessage(error) || 'Failed to load block levels.');
          this.blocksByDistrict.set(districtId, []);
          this.visibleRows = this.buildVisibleRows();
        },
      });
  }

  private buildVisibleRows(): ManageCountryDataRow[] {
    const rows: ManageCountryDataRow[] = [];
    const openStateId = this.expandedStateId;
    const openDistrictId = this.expandedDistrictId;
    const openDistrictBlocks = openDistrictId !== null ? this.blocksByDistrict.get(openDistrictId) ?? [] : [];

    for (const state of this.stateRows) {
      rows.push(state);

      if (openStateId !== state.id) {
        continue;
      }

      const districts = this.districtsByState.get(state.id) ?? [];
      for (const district of districts) {
        rows.push(district);

        if (openDistrictId !== district.id) {
          continue;
        }

        for (const block of openDistrictBlocks) {
          rows.push(block);
        }
      }
    }

    return rows;
  }

  private extractStateRows(items: ManageCountryDataStateResponseItem[]): ManageCountryDataRow[] {
    return items.map((item) => ({
      id: Number(item.id),
      serial_no: Number(item.serial_no ?? 0),
      level_type: 'state',
      name: item.state_name,
      parent_state_id: null,
      parent_district_id: null,
      depth: 0,
      has_children: true,
    }));
  }

  private extractDistrictRows(items: ManageCountryDataDistrictResponseItem[], stateId: number): ManageCountryDataRow[] {
    return items.map((item) => ({
      id: Number(item.id),
      serial_no: Number(item.serial_no ?? 0),
      level_type: 'district',
      name: item.district_name,
      parent_state_id: stateId,
      parent_district_id: null,
      depth: 1,
      has_children: true,
    }));
  }

  private extractBlockRows(items: ManageCountryDataBlockResponseItem[], districtId: number): ManageCountryDataRow[] {
    const stateId = this.getExpandedStateIdForDistrict(districtId);
    return items.map((item) => ({
      id: Number(item.id),
      serial_no: Number(item.serial_no ?? 0),
      level_type: 'block',
      name: item.local_name,
      parent_state_id: stateId,
      parent_district_id: districtId,
      depth: 2,
      has_children: false,
    }));
  }

  private getExpandedStateIdForDistrict(districtId: number): number | null {
    for (const [stateId, districts] of this.districtsByState.entries()) {
      if (districts.some((district) => district.id === districtId)) {
        return stateId;
      }
    }
    return this.expandedStateId;
  }

  private applyEditLocally(id: number, levelType: ManageCountryDataLevelType, name: string): void {
    if (levelType === 'state') {
      this.stateRows = this.stateRows.map((row) => (row.id === id ? { ...row, name } : row));
    } else if (levelType === 'district') {
      for (const [stateId, districts] of this.districtsByState.entries()) {
        this.districtsByState.set(
          stateId,
          districts.map((row) => (row.id === id ? { ...row, name } : row)),
        );
      }
    } else {
      for (const [districtId, blockRows] of this.blocksByDistrict.entries()) {
        this.blocksByDistrict.set(
          districtId,
          blockRows.map((row) => (row.id === id ? { ...row, name } : row)),
        );
      }
    }

    this.visibleRows = this.buildVisibleRows();
  }

  private buildQueryString(params: Record<string, string | number | null | undefined>): string {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '' || value === 'all') {
        return;
      }
      queryParams.set(key, String(value));
    });
    const query = queryParams.toString();
    return query ? `?${query}` : '';
  }

  private buildApiUrl(baseRoute: string, params: Record<string, string | number | null | undefined>): string {
    return `${baseRoute}${this.buildQueryString(params)}`;
  }

  private buildExportFileName(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `country_data_${year}${month}${day}_${hours}${minutes}${seconds}.xlsx`;
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  }

  private getApiErrorMessage(error: unknown): string | null {
    if (!error || typeof error !== 'object') {
      return null;
    }

    const errorRecord = error as Record<string, unknown>;
    const message = errorRecord['error'];
    if (typeof message === 'string' && message.trim()) {
      return message.trim();
    }

    return null;
  }
}
